import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useAccount, userStore } from '@/lib/account';
import { api, unwrap } from '@/lib/api/client';
import type { AccessStatus, Announcement, Card, Page, User } from '@/lib/models';

/**
 * Server state.
 *
 * Query keys are `[game, resource, cardExtId]` so a mutation can invalidate a whole
 * game with one prefix. `staleTime` is 30 seconds everywhere: the underlying data
 * changes when a cabinet writes it, which is minutes apart at best, and refetching on
 * every focus change would put a request storm behind alt-tab.
 */
export const STALE_TIME = 30_000;

/** Signed-in state, shared by the shell and every page that needs the default card. */
export function useMe() {
  const account = useAccount();

  const query = useQuery({
    queryKey: ['user', 'me'],
    queryFn: () => unwrap<User>(api.get('api/user/me')),
    staleTime: STALE_TIME,
    enabled: account !== null,
    retry: false,
  });

  // Mirror into the plain store so non-React callers (the api client's error mapping,
  // helpers like `defaultCard`) can read the user without a hook.
  useEffect(() => {
    userStore.set(query.data ?? null);
  }, [query.data]);

  return query;
}

export function useAccessStatus(enabled: boolean) {
  return useQuery({
    queryKey: ['account', 'status'],
    queryFn: () => unwrap<AccessStatus>(api.get('api/account/status')),
    staleTime: STALE_TIME,
    enabled,
    retry: false,
  });
}

export function useAnnouncements() {
  return useQuery({
    queryKey: ['announcements'],
    queryFn: () => unwrap<Page<Announcement> | Announcement[]>(api.get('api/user/announcement/')),
    staleTime: STALE_TIME,
  });
}

export function useCards() {
  return useQuery({
    queryKey: ['user', 'cards'],
    queryFn: () => unwrap<Card[]>(api.get('api/user/profiles')),
    staleTime: STALE_TIME,
  });
}

/**
 * A game endpoint that answers bare.
 *
 * Most `/api/game/*` routes are not enveloped — the server has a test asserting it —
 * so these go straight through `api.get` rather than `unwrap`.
 */
export function useGameQuery<T>(
  key: readonly unknown[],
  path: string,
  params?: Record<string, string | number | undefined>,
  options?: Partial<UseQueryOptions<T>>,
) {
  return useQuery<T>({
    queryKey: key,
    queryFn: () => api.get<T>(path, params),
    staleTime: STALE_TIME,
    retry: false,
    ...options,
  });
}
